import React, { useCallback, useEffect, useState } from 'react';

import { Table } from 'components';

import { auditColumns } from './auditColumns';

import { useSelector, useTranslation } from 'third-party';

import { PersisTables } from 'constants/common';

import { useAudit } from 'modules/audit';

import { appSelectors, auditSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import { APIAudit, APISortOrder } from 'types/api';

export const AuditsList = () => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState(auditColumns);

  const { loadAudits, loadMoreAudits, updateAuditFilters, clearAuditList } =
    useAudit();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const tenantId = useSelector(appSelectors.tenantId);
  const items = useSelector(auditSelectors.auditList);
  const auditsListLoaded = useSelector(auditSelectors.auditsListLoaded);
  const allAuditLoaded = useSelector(auditSelectors.allAuditLoaded);
  const auditFilters = useSelector(auditSelectors.auditFilters);
  const loadAuditsPending = useSelector(auditSelectors.loadAuditPending);
  const loadMoreAuditsPending = useSelector(
    auditSelectors.loadMoreAuditPending,
  );

  useEffect(() => {
    if (tenantId && loadedUserSettings) {
      loadAudits();
    }
  }, [tenantId, auditFilters, loadedUserSettings, loadAudits]);

  useEffect(() => {
    const savedColumns = getFromStorage(PersisTables.Audits, auditColumns);
    if (!savedColumns) {
      setLoadedUserSettings(true);
      return;
    }

    updateAuditFilters(extractFiltersFromSavedColumns(savedColumns));
    setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    return () => {
      clearAuditList();
    };
  }, [clearAuditList]);

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updateAuditFilters({ ...auditFilters, [columnId]: value });
    },
    [auditFilters, columns, updateAuditFilters],
  );

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );

      updateAuditFilters({ ...auditFilters, [columnId]: value });
    },
    [auditFilters, columns, updateAuditFilters],
  );

  const onFilterReset = useCallback(() => {
    updateAuditFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateAuditFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  // const rowOptionsMenu = useCallback(
  //   (item: APIAudit, onRowMenuClose: () => void): React.ReactElement[] => [],
  //   [t, isMobile],
  // );

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string | number | symbol) => {
      const existingSortOrder = auditFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      updateAuditFilters({
        ...auditFilters,
        searchSortOrders: [
          {
            sortField: columnDataId as string,
            sortOrder: newSortOrder,
          },
        ],
      });
    },
    [auditFilters, updateAuditFilters],
  );

  return (
    <>
      <Table<APIAudit>
        draggable
        header={t('auditList.header')}
        subHeader={t('auditList.subHeader')}
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Audits, settings);
        }}
        onResetTableSettings={() => {
          setColumns(auditColumns);
          updateAuditFilters({});
          removeFromStorage(PersisTables.Audits);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        allItemsLoaded={allAuditLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortOrder={auditFilters.searchSortOrders?.[0]?.sortOrder}
        // rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadAuditsPending}
        loadMoreItemsPending={loadMoreAuditsPending}
        onLoadMoreClicked={loadMoreAudits}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={auditsListLoaded}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
    </>
  );
};
